/**
 * portalRouting — routage post-login vers les portails du redesign.
 * PUR (testable).
 *
 * Architecture cible (docs/REDESIGN_PORTAL_MAPPING_MATRIX.md) :
 * 5 portails staff (PILOTER / PRODUIRE / SOUTENIR / CONTRÔLER / AMÉLIORER)
 * + 3 portails M13 (Étudiant / Enseignant / Parent) + Master Shell.
 *
 * Règles arbitrées (2026-07-10) :
 *  - C8 : enseignant simple → M13 Enseignant ; les responsabilités académiques
 *    (coordonnateur, chef de dépt…) sont déjà remappées resp_academique/directeur
 *    par LEGACY_TO_SYSTEM et atterrissent donc sur PRODUIRE/PILOTER.
 *  - C9 : rôle système gestionnaire — sous-routage par rôle legacy :
 *    Gestionnaire Scolarité → PRODUIRE, Agent Comptable → SOUTENIR.
 */

import { MASTER_LANDING_ROUTE } from './platformNavigation';
import { hasLegacyRole, resolveSystemRoleId } from '../utils/roleAccess';

export const REDESIGN_PORTALS = {
  MASTER:         'MASTER',
  PILOTER:        'PILOTER',
  PRODUIRE:       'PRODUIRE',
  SOUTENIR:       'SOUTENIR',
  CONTROLE:       'CONTROLE',
  AMELIORER:      'AMELIORER',
  M13_ETUDIANT:   'M13_ETUDIANT',
  M13_ENSEIGNANT: 'M13_ENSEIGNANT',
  M13_PARENT:     'M13_PARENT',
  M13_PERSONNEL:  'M13_PERSONNEL',
};

/**
 * Route d'atterrissage par portail. Toutes ces routes existent — aucune
 * route décorative. Toutes sont gated par capacité (buildRegistryRoutes),
 * SAUF M13_PARENT : /parents est un shell standalone hors Dashboard.
 *
 * CE COMMENTAIRE DISAIT FAUX JUSQU'AU 2026-08-30. Il décrivait encore le
 * modèle du 2026-07-13 : « authentification propre (parent_access /
 * authenticate_parent — jamais profiles), car un parent n'a pas de compte
 * profiles ». Ce n'est plus vrai depuis l'identité unifiée du 2026-07-27, et
 * la mesure du 2026-08-30 sur la production le confirme : `authenticate_parent`
 * n'existe plus, et `get_my_parent_children()` CRÉE un profil de rôle `Parent`
 * à la volée quand l'adresse du jeton n'est pas déjà un profil. Le parent ouvre
 * une session Supabase Auth par lien magique envoyé à l'adresse portée par la
 * fiche de son enfant (règle A6). Voir parentAccessService.js.
 * PILOTER et PRODUIRE restent sur /dashboard (cockpit exécutif / missions)
 * tant que leurs shells dédiés ne sont pas livrés.
 */
export const PORTAL_HOME_ROUTES = {
  [REDESIGN_PORTALS.MASTER]:         MASTER_LANDING_ROUTE,
  [REDESIGN_PORTALS.PILOTER]:        '/dashboard',
  [REDESIGN_PORTALS.PRODUIRE]:       '/dashboard',
  [REDESIGN_PORTALS.SOUTENIR]:       '/dashboard/payments',
  [REDESIGN_PORTALS.CONTROLE]:       '/dashboard/quality-cockpit',
  [REDESIGN_PORTALS.AMELIORER]:      '/dashboard/pmo',
  [REDESIGN_PORTALS.M13_ETUDIANT]:   '/dashboard/portail-etudiant',
  [REDESIGN_PORTALS.M13_ENSEIGNANT]: '/dashboard/portail-enseignant',
  [REDESIGN_PORTALS.M13_PARENT]:     '/parents',
  [REDESIGN_PORTALS.M13_PERSONNEL]:  '/dashboard/portail-personnel',
};

/** Rôles legacy du sous-groupe comptable (C9) — SOUTENIR. */
const COMPTABLE_LEGACY_ROLES = ['Agent Comptable', 'Comptable', 'Caissier'];

const SYSTEM_ROLE_TO_PORTAL = {
  master:          REDESIGN_PORTALS.MASTER,
  dg:              REDESIGN_PORTALS.PILOTER,
  directeur:       REDESIGN_PORTALS.PILOTER,
  resp_academique: REDESIGN_PORTALS.PRODUIRE,
  controle:        REDESIGN_PORTALS.CONTROLE,
  amelioration:    REDESIGN_PORTALS.AMELIORER,
  enseignant:      REDESIGN_PORTALS.M13_ENSEIGNANT,
  etudiant:        REDESIGN_PORTALS.M13_ETUDIANT,
  parent:          REDESIGN_PORTALS.M13_PARENT,
  // Rôles CBAC spécialisés v4 (2026-07-14, respect du nombre d'écrans-portail
  // prescrits — 16 vues dans l'architecture Dashboard unifiée, décision
  // 2026-07-13 non remise en cause) : chacun reste membre d'un des 5 portails
  // existants pour la nav (SECTIONS_BY_PORTAL), mais atterrit sur SA vue
  // dédiée via SYSTEM_ROLE_HOME_ROUTE ci-dessous, pas la route générique du portail.
  rrh:      REDESIGN_PORTALS.SOUTENIR,
  daf:      REDESIGN_PORTALS.SOUTENIR,
  resp_log: REDESIGN_PORTALS.SOUTENIR,
  resp_com: REDESIGN_PORTALS.SOUTENIR,
  resp_rech: REDESIGN_PORTALS.PRODUIRE,
  // P0 frontière plateforme (sous-lot A, 2026-07-23) : l'admin IT d'établissement
  // est un rôle TENANT, portail SOUTENIR — JAMAIS le Master Shell. Son cockpit
  // reste blocked_rbac tant que le serveur et les gardes ne sont pas corrigés
  // (sous-lots C/F/G) ; aucun SYSTEM_ROLE_HOME_ROUTE ne lui est encore attribué.
  it_admin: REDESIGN_PORTALS.SOUTENIR,
};

/**
 * Atterrissage spécifique par RÔLE — prime sur PORTAL_HOME_ROUTES (atterrissage
 * générique du portail). Les rôles CBAC spécialisés v4 atterrissent directement
 * sur leur vue dédiée existante tout en gardant la nav complète de leur portail.
 */
const SYSTEM_ROLE_HOME_ROUTE = {
  // Corrigé 2026-07-16 (audit portails rôles restants) : pointait vers
  // /dashboard/hr (HRView.jsx legacy, formulaires bruts) — orphelin depuis le
  // repointage TENANT_NAV du rollout Soutenir (2026-07-16, commit 42044ed) qui
  // a fait de hr-cockpit (HRPremiumView enrichie) la vraie destination RH.
  // 2026-10-03 (verdict B, arbitrage d'Alfred) : hr-cockpit reste l'écran de capacité RH
  // transversal (PremiumViewShell, partagé), intact ; le cockpit MÉTIER du Responsable RH
  // est rrh-cockpit (TenantCockpitShell, role-native). Aucune redirection ni alias.
  rrh:       '/dashboard/rrh-cockpit',
  // 2026-07-22 (verdict B) : /dashboard/finance-cockpit est l'écran de capacité
  // financière GÉNÉRIQUE (PremiumViewShell), partagé par master/dg/gestionnaire/
  // legacy IT — il reste intact et accessible par ses usages propres. Le cockpit
  // MÉTIER du DAF est daf-cockpit (TenantCockpitShell, 9 rubriques). Aucune
  // redirection ni alias entre les deux.
  daf:       '/dashboard/daf-cockpit',
  // 2026-10-03 (verdict B, arbitrage d'Alfred) : /dashboard/logistique reste l'écran de capacité
  // Logistique (LogisticsAssetsPremiumView, partagé), intact ; le cockpit MÉTIER est resp-log-cockpit.
  resp_log:  '/dashboard/resp-log-cockpit',
  // CommunicationPremiumView reconstruite avec de vraies données (2026-07-14,
  // correctif suite audit — admission_campaigns/notifications réels) : atterrit
  // désormais sur son propre cockpit au lieu du Marketing provisoire.
  resp_com:  '/dashboard/resp-com-cockpit',
  // Corrigé 2026-07-16 (audit portails rôles restants) : pointait vers
  // /dashboard/recherche (RechercheView.jsx, CRUD legacy — dont les
  // formulaires « Ajouter » envoient des champs qui ne correspondent à
  // AUCUNE colonne réelle des tables research_*, signalé mais non corrigé
  // ici, hors périmètre de cet audit) — repointé vers le cockpit Premium
  // View Framework, cohérent avec la convention `-cockpit` de tous les
  // autres rôles responsables de ce chantier (rrh, daf, resp_qualite...).
  // 2026-10-03 (verdict B, arbitrage d'Alfred) : recherche-cockpit reste l'écran de capacité
  // Recherche (PremiumViewShell, partagé), intact ; le cockpit MÉTIER est resp-rech-cockpit.
  resp_rech: '/dashboard/resp-rech-cockpit',
};

/**
 * Atterrissage spécifique par RÔLE LEGACY — prime sur tout le reste.
 * 'IT' : reconnu tenant-admin complet au niveau RLS (auth_is_tenant_admin(),
 * au même titre que Master/DG/Administrateur) mais reste un membre du TENANT —
 * ne doit JAMAIS atterrir sur le Master Shell (bug pré-existant : legacyRole
 * 'IT' résout sysRoleId 'master' via LEGACY_TO_SYSTEM, donc SYSTEM_ROLE_TO_PORTAL
 * l'envoyait à tort vers MASTER_LANDING_ROUTE — corrigé 2026-07-14).
 */
const LEGACY_ROLE_HOME_ROUTE = {
  // it_admin (2026-07-16, couche Portails Rôles CBAC) remplace l'atterrissage
  // générique par son propre cockpit (Organigramme/Annuaire/Devise/CBAC —
  // Quick Access vers Rôles & Permissions/Affectations/Migration Wizard).
  IT: '/dashboard/it-admin-cockpit',
  // Portail rôle resp_scol (2026-07-16, couche Portails Rôles CBAC) :
  // atterrissage dédié plutôt que la route Produire générique — clé sur le
  // rôle LEGACY (pas `sysRoleId`, qui vaudrait 'gestionnaire' au même titre
  // qu'Agent Comptable, déjà géré séparément dans `resolveHomePortal`).
  'Gestionnaire Scolarité': '/dashboard/resp-scol-cockpit',
  // Portail rôle doyen (2026-07-16, couche Portails Rôles CBAC) :
  // atterrissage dédié plutôt que la route Piloter générique — clé sur le
  // rôle LEGACY précis (pas `sysRoleId`, qui vaudrait 'directeur' au même
  // titre que Directeur / Chef de Département / Directeur Académique, qui
  // gardent leur atterrissage générique).
  Doyen: '/dashboard/doyen-cockpit',
  // Portail rôle resp_acad (2026-07-16, couche Portails Rôles CBAC) :
  // atterrissage dédié pour le Responsable Académique. Clé legacy précise.
  'Responsable Académique': '/dashboard/resp-acad-cockpit',
  // Rafale 2 (2026-07-16) : coordonnateurs académiques + développement
  // institutionnel — tous partagent sysRoleId `resp_academique` avec
  // Responsable Académique ci-dessus, distingués ici par leur rôle LEGACY
  // précis. 'Référent de Spécialité'/'Chargé Académique' gardent encore
  // l'atterrissage Produire générique (aucun portail rôle dédié construit
  // pour eux à ce jour).
  'Coordonnateur de Cycle': '/dashboard/coord-cycle-cockpit',
  'Coordonnateur de Programme': '/dashboard/coord-filiere-cockpit',
  'Coordonnateur de Niveau': '/dashboard/coord-niveau-cockpit',
  'Responsable Développement': '/dashboard/dev-inst-cockpit',
  // Portails rôles resp_qualite/resp_conform (rafale 2, 2026-07-16) —
  // partagent sysRoleId `controle`, distingués par leur rôle LEGACY précis.
  'Responsable Qualité': '/dashboard/resp-qualite-cockpit',
  'Responsable Conformité': '/dashboard/resp-conform-cockpit',
  // Note : 'Manager Qualité' (aussi `controle`) garde l'atterrissage
  // générique Contrôler — libellé alternatif sans portail rôle propre.
};

/**
 * Registre FERMÉ des modèles de cockpit (`tenant_roles.cockpit_template_key`,
 * Lot D1 du chantier rôles dynamiques, 2026-07-16 — plan §3ter). 23 clés :
 * les 15 cockpits de rôle individuels (le CAS est un espace collégial, pas
 * un rôle individuel — jamais de clé `cas`) + un repli générique par portail
 * Fonction. Ce module reste SEUL responsable de traduire une clé en route
 * réelle — la base ne stocke jamais une URL. Clé inconnue → null, jamais une
 * route devinée.
 */
const normalizeLegacyRoleKey = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

const NORMALIZED_LEGACY_ROLE_HOME_ROUTE = Object.fromEntries(
  Object.entries(LEGACY_ROLE_HOME_ROUTE)
    .map(([role, route]) => [normalizeLegacyRoleKey(role), route])
);

export const COCKPIT_TEMPLATE_HOME_ROUTE = {
  doyen:         '/dashboard/doyen-cockpit',
  resp_acad:     '/dashboard/resp-acad-cockpit',
  resp_scol:     '/dashboard/resp-scol-cockpit',
  resp_qualite:  '/dashboard/resp-qualite-cockpit',
  resp_conform:  '/dashboard/resp-conform-cockpit',
  coord_cycle:   '/dashboard/coord-cycle-cockpit',
  coord_filiere: '/dashboard/coord-filiere-cockpit',
  coord_niveau:  '/dashboard/coord-niveau-cockpit',
  dev_inst:      '/dashboard/dev-inst-cockpit',
  it_admin:      '/dashboard/it-admin-cockpit',
  // Chemin DYNAMIQUE (cockpit_template_key) — prioritaire sur la table statique
  // quand rbacMode === 'dynamic'. Les deux doivent bouger ensemble, sinon
  // l'atterrissage diverge entre les modes legacy et dynamique.
  daf:           '/dashboard/daf-cockpit',
  rrh:           '/dashboard/rrh-cockpit',
  resp_com:      '/dashboard/resp-com-cockpit',
  resp_log:      '/dashboard/resp-log-cockpit',
  resp_rech:     '/dashboard/resp-rech-cockpit',
  // Rôle DYNAMIQUE du tenant, sans libellé legacy ni rôle système (arbitrage d'Alfred, 2026-10-03) :
  // il n'existe que par ce gabarit. La clé doit aussi figurer dans la contrainte CHECK de
  // tenant_roles.cockpit_template_key (migration 20261003250000).
  dir_campus:    '/dashboard/dir-campus-cockpit',
  // Admissions (rattaché au Gestionnaire Scolarité, sans atterrissage propre) et Vie étudiante (rôle dynamique) :
  // arbitrages d'Alfred du 2026-10-03. Les clés doivent aussi figurer dans la contrainte CHECK de
  // tenant_roles.cockpit_template_key (migration 20261003260000). Le shell mémorise l'état d'un cockpit
  // par cette clé : tout cockpit monté sur TenantCockpitShell en a une.
  resp_admissions: '/dashboard/resp-admissions-cockpit',
  vie_etudiante:   '/dashboard/vie-etudiante-cockpit',
  // Gouvernance et Conseils (CA + CAS), rôle dynamique (arbitrage d'Alfred, 2026-10-03) — migration 20261003270000.
  gouvernance_conseil: '/dashboard/gouvernance-conseil-cockpit',
  // Secrétaire général, rôle dynamique (arbitrage d'Alfred, 2026-10-03) — migration 20261003280000.
  sec_general: '/dashboard/sec-general-cockpit',
  // ── UN ATTERRISSAGE POUR LES RÔLES SANS COCKPIT MÉTIER ──────────────────
  // Mesuré le 2026-09-16 : QUATRE rôles du gabarit sur 28 —
  // `alumni_international`, `juridique`, `personnel`, `rh_onboarding_admin` —
  // portent `generic_soutenir`, dont l'atterrissage est `/dashboard/payments`,
  // gardé par `finance:read`. Aucun des quatre ne détient cette capacité : ils
  // étaient REFUSÉS à l'écran que leur propre gabarit leur assigne, et
  // rebondissaient sans message vers un second décideur de route.
  //
  // Le Launchpad est la bonne destination pour eux : il ne présuppose AUCUNE
  // capacité métier, et il montre précisément ce à quoi la personne a accès.
  // Un accueil de portail ne doit pas exiger un droit que ses membres n'ont
  // pas tous — sinon il n'est pas un accueil.
  generic_launchpad: '/dashboard/launchpad',
  generic_piloter:   PORTAL_HOME_ROUTES[REDESIGN_PORTALS.PILOTER],
  generic_produire:  PORTAL_HOME_ROUTES[REDESIGN_PORTALS.PRODUIRE],
  generic_soutenir:  PORTAL_HOME_ROUTES[REDESIGN_PORTALS.SOUTENIR],
  generic_controler: PORTAL_HOME_ROUTES[REDESIGN_PORTALS.CONTROLE],
  generic_ameliorer: PORTAL_HOME_ROUTES[REDESIGN_PORTALS.AMELIORER],
  // Les TROIS portails M13, ajoutés le 2026-09-09 sur le même motif que les
  // replis génériques ci-dessus : une clé, une route déjà câblée.
  //
  // POURQUOI ILS MANQUAIENT, ET CE QUE ÇA COÛTAIT. Le registre couvrait les
  // quinze cockpits du PERSONNEL ; étudiant, enseignant et parent n'en sont
  // pas — ce sont des portails. Résultat mesuré en production le 2026-09-09 :
  // douze `tenant_roles` (trois codes × quatre établissements) sans
  // `cockpit_template_key`, portant **346 des 449 affectations, soit 77 %**.
  // Tous les tenants étant en `rbac_mode = dynamic`, ces 77 % ne passaient PAS
  // par le chemin dynamique : ils tombaient dans le pont legacy de
  // `resolveHomeRoute`.
  //
  // Ils atterrissaient bien — le pont fonctionne, et le chantier
  // `douze-roles-sans-cockpit` disait lui-même que « se connectent et
  // n'arrivent nulle part » était FAUX. Ce n'était pas une panne, c'était une
  // dette : retirer le pont legacy aurait renvoyé 77 % des comptes sur
  // `/dashboard`, et rien n'aurait signalé pourquoi.
  //
  // Les routes ci-dessous sont EXACTEMENT celles que le pont produisait déjà :
  // ce n'est pas un changement de comportement, c'est le même atterrissage
  // rendu explicite et autoritaire.
  portail_etudiant:   PORTAL_HOME_ROUTES[REDESIGN_PORTALS.M13_ETUDIANT],
  portail_enseignant: PORTAL_HOME_ROUTES[REDESIGN_PORTALS.M13_ENSEIGNANT],
  portail_parent:     PORTAL_HOME_ROUTES[REDESIGN_PORTALS.M13_PARENT],
};

export const COCKPIT_TEMPLATE_KEYS = Object.freeze(Object.keys(COCKPIT_TEMPLATE_HOME_ROUTE));

/** Traduit une clé de modèle en route réelle — null si clé inconnue (rejet
 * strict, jamais une route devinée). */
export function resolveCockpitTemplateRoute(key) {
  return COCKPIT_TEMPLATE_HOME_ROUTE[key] || null;
}

/**
 * Table INVERSE route → clé de modèle. Les clés sont en snake_case
 * (`coord_cycle`) alors que les routes sont en kebab-case
 * (`/dashboard/coord-cycle-cockpit`) : dériver l'une de l'autre par
 * manipulation de chaîne produit `coord-cycle` et rate la cible.
 *
 * C'est exactement ce que faisait `BreadcrumbBar` pour retrouver l'état
 * mémorisé par `TenantCockpitShell` (`last_cockpit_url_<cockpitId>`) : 10 des
 * 13 cockpits — tous ceux dont la clé comporte plus d'un mot — ne
 * retrouvaient jamais leur clé et perdaient onglet, filtres et défilement au
 * retour (audit 2026-08-16). Les entrées `generic_*` sont écartées : ce sont
 * des accueils de portail partagés, pas des cockpits à état.
 */
const COCKPIT_TEMPLATE_KEY_BY_ROUTE = Object.freeze(
  Object.fromEntries(
    Object.entries(COCKPIT_TEMPLATE_HOME_ROUTE)
      .filter(([key]) => !key.startsWith('generic_'))
      .map(([key, route]) => [route, key]),
  ),
);

/**
 * Clé de modèle d'une route de cockpit — `null` si la route n'est pas un
 * cockpit à état connu (jamais une clé devinée). C'est la SOURCE UNIQUE de
 * l'identifiant utilisé pour mémoriser et restaurer l'état d'un cockpit.
 */
export function resolveCockpitTemplateKey(route) {
  return COCKPIT_TEMPLATE_KEY_BY_ROUTE[route] || null;
}

/**
 * Routes d'atterrissage DÉDIÉES à un rôle précis (les 16 cockpits tenant
 * CBAC — `SYSTEM_ROLE_HOME_ROUTE`/`LEGACY_ROLE_HOME_ROUTE` ci-dessus) —
 * distinctes des routes génériques de portail (`PORTAL_HOME_ROUTES`), qui
 * servent toute l'équipe d'une fonction, pas un rôle précis. Utilisé par
 * `BreadcrumbBar` pour l'affordance « retour à mon cockpit » (Lot 1,
 * chantier uniformisation des cockpits tenant, 2026-07-16).
 */
export const DEDICATED_COCKPIT_HOME_ROUTES = new Set([
  ...Object.values(SYSTEM_ROLE_HOME_ROUTE),
  ...Object.values(LEGACY_ROLE_HOME_ROUTE),
]);

export function isDedicatedCockpitHomeRoute(route) {
  return DEDICATED_COCKPIT_HOME_ROUTES.has(route);
}

/**
 * Rôle(s) SYSTÈME propriétaire(s) de chaque cockpit dédié.
 *
 * Un cockpit dédié est l'atterrissage d'un LIBELLÉ de fonction, mais la
 * frontière d'autorité réelle est le rôle SYSTÈME : c'est lui qui porte les
 * capacités (`capability-registry.json`). Deux libellés qui résolvent vers le
 * même rôle système ont, par construction, exactement les mêmes capacités —
 * les cloisonner l'un de l'autre n'ajoute aucune protection, cela ne fait
 * qu'interdire une navigation légitime.
 *
 * Arbitrage Alfred (2026-08-12) : « Coordonnateur de Cycle » et « Responsable
 * Académique » ne sont pas des fonctions souvent distinctes — la même personne
 * les cumule dans la plupart des établissements. Les quatre cockpits académiques
 * (resp_acad, coord_cycle, coord_filiere, coord_niveau) partagent effectivement
 * le rôle système `resp_academique` ; ils doivent donc être mutuellement
 * atteignables. La séparation DAF / resp_com, elle, oppose deux rôles systèmes
 * DISTINCTS et reste entière (défaut du 2026-07-22).
 *
 * `dev_inst` (Développement Institutionnel & Marketing) en est sorti le
 * 2026-10-04 : arbitrage utilisateur, rôle système dédié (capacité `marketing`
 * en écriture, que `resp_academique` ne porte pas) — n'est donc plus
 * mutuellement atteignable avec les quatre cockpits académiques ci-dessus.
 *
 * La barrière fine demeure le CBAC : `coord-filiere-cockpit` exige `teaching`,
 * `coord-niveau-cockpit` exige `student_lifecycle`, etc. Le portail n'a jamais
 * eu vocation à remplacer la capacité.
 */
const COCKPIT_OWNER_SYSTEM_ROLES = (() => {
  const parRoute = new Map();
  const ajouter = (route, sysRoleId) => {
    if (!route || !sysRoleId) return;
    if (!parRoute.has(route)) parRoute.set(route, new Set());
    parRoute.get(route).add(sysRoleId);
  };
  for (const [sysRoleId, route] of Object.entries(SYSTEM_ROLE_HOME_ROUTE)) {
    ajouter(route, sysRoleId);
  }
  for (const [legacyRole, route] of Object.entries(LEGACY_ROLE_HOME_ROUTE)) {
    ajouter(route, resolveSystemRoleId(legacyRole));
  }
  return parRoute;
})();

/**
 * @param {string} route — chemin absolu d'un cockpit dédié
 * @returns {Set<string>|null} — rôles systèmes propriétaires, ou null si la
 *   route n'est pas un cockpit dédié connu (l'appelant décide alors, jamais
 *   de propriétaire deviné).
 */
export function cockpitOwnerSystemRoles(route) {
  return COCKPIT_OWNER_SYSTEM_ROLES.get(route) || null;
}

/**
 * Résout le portail d'atterrissage d'un utilisateur.
 * @param {{ sysRoleId?: string, legacyRole?: string, staffOnboardingStatus?: string }} ctx
 * @returns {string} — une valeur de REDESIGN_PORTALS
 */
export function resolveHomePortal({ sysRoleId = '', legacyRole = '', staffOnboardingStatus = '' } = {}) {
  // Personnel « en attente d'affectation » (Wizard RH, étape 1 seule faite) :
  // atterrit strictement sur son Portail Personnel jusqu'à validation RH/DG,
  // quel que soit le rôle transitoire porté par le compte (Lot 6, 2026-07-13).
  if (staffOnboardingStatus === 'attente') return REDESIGN_PORTALS.M13_PERSONNEL;
  // IT tenant : voir LEGACY_ROLE_HOME_ROUTE ci-dessus — jamais REDESIGN_PORTALS.MASTER.
  if (hasLegacyRole({ role: legacyRole }, 'IT')) return REDESIGN_PORTALS.SOUTENIR;
  if (sysRoleId === 'gestionnaire') {
    return COMPTABLE_LEGACY_ROLES.includes(legacyRole)
      ? REDESIGN_PORTALS.SOUTENIR
      : REDESIGN_PORTALS.PRODUIRE;
  }
  return SYSTEM_ROLE_TO_PORTAL[sysRoleId] || REDESIGN_PORTALS.PRODUIRE;
}

/**
 * Tous les portails d'une personne : le sien (`resolveHomePortal`), puis ceux des rôles système qu'elle ENDOSSE
 * par affectation (`ctx.endorsedSysRoleIds`, voir `rolesSystemeEndosses`). Le premier est son portail
 * d'atterrissage. Un IT coopté IT d'école a Soutenir ET Piloter ; un profil sans rôle système (nouvel IT d'école)
 * n'est pas rangé dans le repli PRODUIRE mais dans le portail qu'il endosse. Les portails M13 et Master restent
 * seuls : leur menu est dédié.
 * @returns {string[]} — valeurs de REDESIGN_PORTALS, sans doublon
 */
export function resolvePortals(ctx = {}) {
  const home = resolveHomePortal(ctx);
  if (home === REDESIGN_PORTALS.MASTER || home.startsWith('M13_')) return [home];
  const endossees = (ctx.endorsedSysRoleIds || []).map((id) => SYSTEM_ROLE_TO_PORTAL[id]).filter(Boolean);
  const propre = ctx.sysRoleId || !endossees.length ? [home] : [];
  return [...new Set([...propre, ...endossees])];
}

/**
 * Route d'atterrissage post-login.
 * En mode `dynamic`, la clé de modèle de cockpit du rôle AFFECTÉ
 * (`ctx.cockpitTemplateKey`, chargée par l'appelant depuis
 * `user_role_assignments`→`tenant_roles.cockpit_template_key`) prime sur le
 * pont legacy (Lot D1 rôles dynamiques — le routage cesse d'être aveugle aux
 * rôles clonés/créés). Clé inconnue ou mode legacy → chaîne existante
 * inchangée. Reste pur/synchrone : la traduction clé→route est un lookup.
 * @returns {string} — chemin absolu
 */
export function resolveHomeRoute(ctx = {}) {
  const { sysRoleId = '', legacyRole = '', rbacMode = '', cockpitTemplateKey = '' } = ctx;
  if (rbacMode === 'dynamic' && cockpitTemplateKey) {
    const templated = resolveCockpitTemplateRoute(cockpitTemplateKey);
    if (templated) return templated;
  }
  const legacyHome = LEGACY_ROLE_HOME_ROUTE[legacyRole]
    || NORMALIZED_LEGACY_ROLE_HOME_ROUTE[normalizeLegacyRoleKey(legacyRole)];
  if (legacyHome) return legacyHome;
  if (SYSTEM_ROLE_HOME_ROUTE[sysRoleId]) return SYSTEM_ROLE_HOME_ROUTE[sysRoleId];
  return PORTAL_HOME_ROUTES[resolveHomePortal(ctx)] || '/dashboard';
}
